import * as yup from "yup";

import {
  Button,
  KIND,
  SIZE as BUTTON_SIZE,
  TButtonProps,
} from "kanopi-components/Button";
import React, { FC, useRef, useState, useCallback } from "react";

import { Block } from "kanopi-components/Block";
import { CardBody } from "kanopi-components/Card";
import { Form } from "kanopi-components/Form";
import { FormControl } from "kanopi-components/Input";
import { HeadingSmall } from "kanopi-components/typography";
import { Select } from "kanopi-components/Select";
import { Modal } from "kanopi-components/Modal";
import { useAPI } from "kw/api/useAPI";
import { useForm } from "react-weaver";
import { useGlobal } from "kanopi-components/useGlobal";
import { useNavigate } from "@reach/router";
import { AdminCreateLazyUserButton } from "kw/admin/components/AdminCreateLazyUserButton";
import { useCustomers } from "kw/admin/hooks/useCustomers";
import { useUser } from "kw/auth/useUser";
import { useGate } from "statsig-react";

type TQuotePageCreateQuoteModal = {
  customer: TCustomer;
  products: TProduct[];
  distributionPartners: TDistributionPartner[];
  closeModal: () => void;
  addCustomerCallback?: () => void;
};


const QuotePageCreateQuoteModal: FC<TQuotePageCreateQuoteModal> = (
  {
    products,
    distributionPartners,
    closeModal, addCustomerCallback,
  }) => {
  const navigate = useNavigate();
  const [, setHijack] = useGlobal("hijack");
  const [, setQuotingData] = useGlobal("quoting");
  const { customers: customerOptions, loading, fetchCustomers, searchCustomers } = useCustomers();
  const { value: isQBE, isLoading } = useGate("qbe_admin");

  const user = useUser();
  const userFullName = `${user?.firstName} ${user?.lastName}`;

  const getDpKeyByName = (distributionPartners: {
    key: string;
    name: string
  } [], targetName: string): string | undefined => {
    return distributionPartners.find(distributionPartner => distributionPartner.name === targetName)?.key ?? "";
  };
  const defaultDistributionPartnerKey = getDpKeyByName(distributionPartners, userFullName);

  const { fieldProps, errors, values, notReady, updateValues } = useForm({
    validator: yup.object().shape({
      customerId: yup.string().required().label("Customer"),
      product: yup.string().required().label("Product"),
      distributionPartner: yup.string().label("Distribution Partner"),
    }),
    initialValues: {
      distributionPartner: defaultDistributionPartnerKey ?? "",
      product: products[0]?.key ? [products[0].key] : [],
    },
  });
  const productLink = React.useMemo(() => {
    const productKey = values["product"];
    if (productKey) {
      return productKey
        .map((key: string) => `product=${encodeURIComponent(key)}`)
        .join("&");
    }
  }, [values]);

  const quoteUrl = React.useMemo(() => {
    return `/getquote?${productLink}&distributionPartner=${values["distributionPartner"]}`;
  }, [productLink, values["distributionPartner"]]);

  const handleSubmit = (overrideCustomerId?: string) => {
    const productKey = values["product"];
    const customerId = overrideCustomerId ?? values["customerId"];

    if (!productKey || !customerId) return;

    setQuotingData({
      enabledProducts: productKey,
    });

    const shouldAutoQuote =
      products.find((p: TProduct) => p.key == productKey)?.autoQuote || false;
    const nextUrl = shouldAutoQuote ? "/autoquote" : quoteUrl;
    setHijack({
      nextUrl,
      releaseToUrl: "/admin/quotes",
    });
    navigate(`/user-hijacking/hijack/${customerId}`);
  };

  const [extraCustomer, setExtraCustomer] = useState<TCustomer | null>(null);
  const handleAddCustomer = (user: TUser) => {
    addCustomerCallback?.();
    updateValues({ customerId: user.id });

    const patchedCustomer = {
      id: user.id,
      email: user.email ?? "", // add default/fallback
      fullName: user.fullName ??
        `${user.firstName || ""} ${user.lastName || ""}`.trim(),
    };

    setExtraCustomer(patchedCustomer as TCustomer);

    const hasValidProduct = Array.isArray(values.product) && values.product.length > 0;

    if (hasValidProduct) {
      handleSubmit(user.id);
    }
  };

  const handleCustomerSearch = (event: { target: { value: string } }) => {
    const value = event.target.value;
    if (value && value.length >= 2) {
      searchCustomers(value);
    }
  };
  const filterOptions = (options: any) => {
    return options;
  };

  const getLabel = useCallback(
  (option: TCustomer) => {
    if (!option) return "-";
    const name = option.fullName?.trim() || "-";
    const email = option.email?.trim() || "-";
    return isQBE ? name : `${name} (${email})`;
  },
  [isQBE]
);

  return (
    <Modal onClose={() => closeModal()} closeable isOpen={true} animate autoFocus>
      <Modal.Body $style={{ marginTop: "64px" }}>
        <Form>
          <CardBody
            display="grid"
            gridGap="scale900"
            gridTemplateColumns={["1fr"]}
            justifyItems="center"
            paddingBottom="scale900"
            paddingTop="scale900"
          >
            <Block>
              <HeadingSmall margin={0}>Create a quote</HeadingSmall>
            </Block>

            <Block width="100%">
              <FormControl
                label="Customer"
                error={errors?.customerId}
                forceFocus={true}
              >
                <Select
                  id="select-customer"
                  options={[
                    ...(extraCustomer ? [extraCustomer] : []),
                    ...(customerOptions?.items || []),
                  ]}
                  getOptionLabel={({ option }) => getLabel(option)}
                  getValueLabel={({ option }) => getLabel(option)}
                  onInputChange={handleCustomerSearch}
                  filterOptions={filterOptions}
                  valueKey="id"
                  placeholder="Type to search customers"
                  searchable={true}
                  {...fieldProps.customerId}
                />
              </FormControl>
            </Block>
            <Block width="100%">
              <AdminCreateLazyUserButton
                key={0}
                size={BUTTON_SIZE.compact}
                outlined={true}
                callBack={handleAddCustomer}
                buttonText={"Add customer and create quote"}
              />
            </Block>
            <Block width="100%">
              <FormControl label="Product name" error={errors?.product}>
                <Select
                  id="select-product"
                  options={products}
                  labelKey="name"
                  valueKey="key"
                  placeholder="Choose a product"
                  multi={true}
                  {...fieldProps.product}
                />
              </FormControl>
            </Block>
            <FormControl label="Partner" error={errors?.distributionPartner}>
              <Select
                id="select-distribution-partner"
                options={[{ key: "", name: "None" }, ...distributionPartners]}
                labelKey="name"
                valueKey="key"
                placeholder="Choose a partner"
                {...fieldProps.distributionPartner}
              />
            </FormControl>
            <Button
              type="submit"
              kind={KIND.secondary}
              onClick={() => handleSubmit()}
              disabled={notReady}
            >
              Create a quote
            </Button>
          </CardBody>
        </Form>
      </Modal.Body>
    </Modal>
  );
};

type TQuotePageCreateQuoteButton = {
  customer: TCustomer;
} & TButtonProps;

export const QuotePageCreateQuoteButton: FC<TQuotePageCreateQuoteButton> = (
  {
    customer,
    ...props
  },
) => {
  const { query } = useAPI();

  const [isOpen, setIsOpen] = useState(false);
  const [products, setProducts] = useState([]);
  const [distributionPartners, setDistributionPartners] = useState([]);
  const getProducts = () => {
    query(
      {
        admin: {
          products: {
            name: true,
            key: true,
            autoQuote: true,
          },
          distributionPartners: {
            key: true,
            name: true,
          },
        },
      },
      (response: {
        admin: {
          products: TProduct[];
          distributionPartners: TDistributionPartner[];
        };
      }) => {
        setProducts(response.admin.products);
        setDistributionPartners(response.admin.distributionPartners);
        setIsOpen(true);
      },
    );
  };

  const handleModalOpen = () => {
    if (products.length === 0) {
      getProducts();
    } else {
      setIsOpen(true);
    }
  };

  const handleModalClose = () => {
    setIsOpen(false);
  };

  const addCustomerCallback = () => {
    getProducts();
  };

  return (
    <>
      <Button
        size={BUTTON_SIZE.compact}
        outlined={true}
        onClick={handleModalOpen}
        {...props}
      >
        Create quote
      </Button>
      {isOpen && (
        <QuotePageCreateQuoteModal
          products={products}
          distributionPartners={distributionPartners}
          closeModal={handleModalClose}
          addCustomerCallback={addCustomerCallback}
        />
      )}
    </>
  );
};
